import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, Heart } from 'lucide-react';
import type { WishlistTemplateToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { serverMessage } from '~/lib/api/server-error';
import { useWishlistTemplatesList, useWishlistTemplateMutations } from '~/hooks/use-wishlist-templates';
import { WishlistTemplateEditDialog } from './wishlist-template-edit-dialog';

const Bool = ({ on }: { on: boolean }) =>
	on ? <Check size={16} className="text-success-fg" /> : <span className="text-foreground-disabled">—</span>;

// The list endpoint hydrates ProductIds (membership) but leaves Products empty, so count from ProductIds.
const productCount = (o: WishlistTemplateToken) => o.ProductIds?.length ?? o.Products?.length ?? 0;

/** Wishlist templates (Πρότυπα Wishlist) CRUD surface — parity with the legacy popup (Name/Code/Visible/Sort + products). */
export function WishlistTemplatesGridView() {
	const { t } = useTranslation();
	const { data, isLoading, isError, refetch } = useWishlistTemplatesList();
	const { remove } = useWishlistTemplateMutations();

	const [editOpen, setEditOpen] = useState(false);
	const [editing, setEditing] = useState<WishlistTemplateToken | null>(null);
	const [deleteRow, setDeleteRow] = useState<WishlistTemplateToken | null>(null);

	const columns = useMemo<SystemGridColumn<WishlistTemplateToken>[]>(
		() => [
			{
				key: 'name',
				header: t('catalog.wishlistTemplates.columns.name'),
				width: 'minmax(0,1fr)',
				sortValue: o => o.nm ?? '',
				exportValue: o => o.nm ?? '',
				render: o => (
					<span className="truncate text-sm font-medium text-foreground" title={o.nm ?? ''}>
						{o.nm || '—'}
					</span>
				)
			},
			{
				key: 'code',
				header: t('catalog.wishlistTemplates.columns.code'),
				width: '200px',
				sortValue: o => o.cd ?? '',
				exportValue: o => o.cd ?? '',
				render: o => <span className="truncate font-mono text-xs text-foreground-muted">{o.cd || '—'}</span>
			},
			{
				key: 'products',
				header: t('catalog.wishlistTemplates.columns.products'),
				width: '120px',
				align: 'right',
				sortValue: o => productCount(o),
				exportValue: o => productCount(o),
				render: o => <span className="text-sm tabular-nums text-foreground-muted">{productCount(o)}</span>
			},
			{
				key: 'visible',
				header: t('catalog.wishlistTemplates.columns.visible'),
				width: '90px',
				align: 'center',
				sortValue: o => (o.iv ? 1 : 0),
				exportValue: o => (o.iv ? t('common.yes') : t('common.no')),
				render: o => <Bool on={Boolean(o.iv)} />
			},
			{
				key: 'sort',
				header: t('catalog.wishlistTemplates.columns.sort'),
				width: '80px',
				align: 'right',
				sortValue: o => o.si ?? 0,
				exportValue: o => o.si ?? 0,
				render: o => <span className="text-sm tabular-nums text-foreground-muted">{o.si}</span>
			}
		],
		[t]
	);

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: (err: unknown) => toast.error(serverMessage(err))
		});
	};

	return (
		<>
			<SystemGrid<WishlistTemplateToken>
				gridId="wishlist-templates"
				title={t('catalog.wishlistTemplates.title')}
				rows={data ?? []}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={o => o.id ?? o.nm}
				columns={columns}
				searchPredicate={(o, q) => (o.nm ?? '').toLowerCase().includes(q) || (o.cd ?? '').toLowerCase().includes(q)}
				defaultSort={{ key: 'name', dir: 'asc' }}
				emptyIcon={<Heart size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				addLabel={t('catalog.wishlistTemplates.add')}
				onAdd={() => {
					setEditing(null);
					setEditOpen(true);
				}}
				onEdit={o => {
					setEditing(o);
					setEditOpen(true);
				}}
				onDelete={setDeleteRow}
				exportFilename="wishlist-templates"
			/>

			<WishlistTemplateEditDialog open={editOpen} onOpenChange={setEditOpen} initial={editing} />

			<Dialog open={Boolean(deleteRow)} onOpenChange={open => !open && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('catalog.wishlistTemplates.deleteTitle')}</DialogTitle>
						<DialogDescription>{t('catalog.wishlistTemplates.deleteBody', { name: deleteRow?.nm || '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
